import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';

import ErrorMessage from 'components/ErrorMessage';

import Button from 'primitives/Button';
import { Dialog, DialogActions, DialogPanel, DialogSubTitle, DialogTitle } from 'primitives/Dialog';

export const MAX_REMINDERS_PER_INVITE = 2;
export const REMINDER_COOLDOWN_DAYS = 3;

const SEND_DEAL_INVITE_REMINDERS_MUTATION = gql(`
  mutation SendDealInviteReminders($id: ID!, $inviteIds: [ID!], $selectAll: Boolean) {
    sendDealInviteReminders(id: $id, inviteIds: $inviteIds, selectAll: $selectAll) {
      sent
      skippedMaxReached
      skippedCooldown
      skippedIneligible
    }
  }
`);

const SendInviteRemindersDialogButton: FC<{
  dealId: string;
  selectedInviteIds: string[];
  selectAll: boolean;
  recipientCount: number;
  disabled: boolean;
  onSent: () => void;
}> = ({ dealId, selectedInviteIds, selectAll, recipientCount, disabled, onSent }) => {
  const [open, setOpen] = useState(false);

  const [sendReminders, { data, loading, error, reset }] = useMutation(
    SEND_DEAL_INVITE_REMINDERS_MUTATION,
    { refetchQueries: ['GetActiveInvites'] }
  );

  const result = data?.sendDealInviteReminders;

  function closeDialog() {
    setOpen(false);
    reset();
    if (result) onSent();
  }

  function renderResult() {
    if (!result) return null;
    return (
      <div className="mt-4 text-sm text-gray-600">
        <p className="font-semibold text-gray-900">{result.sent} reminder(s) sent</p>
        {result.skippedMaxReached > 0 && (
          <p>
            {result.skippedMaxReached} skipped: already reminded {MAX_REMINDERS_PER_INVITE} times
          </p>
        )}
        {result.skippedCooldown > 0 && (
          <p>
            {result.skippedCooldown} skipped: reminded within the last {REMINDER_COOLDOWN_DAYS} days
          </p>
        )}
        {result.skippedIneligible > 0 && (
          <p>
            {result.skippedIneligible} skipped: already clicked through, email bounced, blocked, or
            no longer invited
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      <Button disabled={disabled || recipientCount === 0} onClick={() => setOpen(true)}>
        Send Reminder
      </Button>
      <Dialog open={open} onClose={closeDialog}>
        <DialogPanel>
          <DialogTitle>Send Reminder</DialogTitle>
          <DialogSubTitle>
            Each investor can be reminded up to {MAX_REMINDERS_PER_INVITE} times, at least{' '}
            {REMINDER_COOLDOWN_DAYS} days apart. Investors who do not qualify are skipped.
          </DialogSubTitle>
          {result ? (
            renderResult()
          ) : (
            <p>
              Send a reminder to <span className="font-bold">{recipientCount}</span> invited
              investor(s)? Anyone who has already clicked through to the deal, or whose email
              bounced, is skipped.
            </p>
          )}
          {error && <ErrorMessage error={error} />}
          <DialogActions>
            <Button variant="secondary" onClick={closeDialog}>
              {result ? 'Close' : 'Cancel'}
            </Button>
            {!result && (
              <Button
                loading={loading}
                onClick={() =>
                  sendReminders({
                    variables: {
                      id: dealId,
                      inviteIds: selectAll ? [] : selectedInviteIds,
                      selectAll,
                    },
                  })
                }
              >
                Send
              </Button>
            )}
          </DialogActions>
        </DialogPanel>
      </Dialog>
    </>
  );
};

export default SendInviteRemindersDialogButton;
